import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';
import { type ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { AlertBox } from '@/common/AlertBox';
import { CongregationDashboard } from '~/modules/dashboard/CongregationDashboard';
import { StudyHelperDashboard } from '~/modules/dashboard/StudyHelperDashboard';

export const Route = createFileRoute('/_app/_dashboard/groups/$groupName/')({
  component: RouteComponent,
});

function RouteComponent(): ReactElement | null {
  const { groupName } = useParams({
    from: '/_app/_dashboard/groups/$groupName/',
  });
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    data: currentGroup,
    isPending,
    isError,
  } = api.getGroupBySlug.useQuery({
    path: {
      group_slug: groupName,
    },
  });

  // Legacy's default tab is the dashboard for every group type except social,
  // which redirects to activity. Legacy has no social dashboard, so rendering
  // the congregation one would show widgets it never serves for a social group.
  const isSocial = currentGroup?.group_type === 'SOCIAL_GROUP';

  useEffect(() => {
    if (isSocial) {
      navigate({
        to: '/groups/$groupName/activity/',
        params: { groupName },
        replace: true,
      });
    }
  }, [isSocial, groupName, navigate]);

  // A refused or unknown group resolves the screen, as legacy answers "Page not
  // found" to a non-member of a hidden group.
  //
  // This only fires when the slug read fails, i.e. on a hidden group. On a
  // public study group the slug read succeeds for a stranger and the refusal
  // arrives on the stats query, which `StudyHelperDashboard` handles.
  if (isError) {
    return <AlertBox variant="error">{t('wbs.page-not-found')}</AlertBox>;
  }

  // Hold the frame until the type is known: the last branch is no longer a
  // catch-all default, and mounting the congregation dashboard for a beat fires
  // its widget queries against a group that will never render it.
  if (isPending || isSocial) return null;

  // An explicit switch: untyped groups, including the retired follow-up groups,
  // get "Page not found" rather than a congregation stat bar.
  // `CUSTOM_STUDY_GROUP` shares the congregation dashboard, as in legacy.
  // `SOCIAL_GROUP` never gets here; it's redirected to `/activity/` above.
  switch (currentGroup?.group_type) {
    case 'STUDY_HELPER_STUDY_GROUP':
      return <StudyHelperDashboard />;
    case 'CONGREGATION':
    case 'CUSTOM_STUDY_GROUP':
      return <CongregationDashboard groupName={groupName} />;
    default:
      return <AlertBox variant="error">{t('wbs.page-not-found')}</AlertBox>;
  }
}
